<script setup lang="ts">
import {computed, onBeforeUnmount, provide, ref, watch} from 'vue'
import type {RouteLocationNormalizedLoadedGeneric} from 'vue-router'
import {viewDepthKey} from 'vue-router'
import CustomRouterView from '@toolify/client/src/modules/routes/components/CustomRouterView.vue'
import {useOverlay} from '@toolify/client/src/modules/modals/composables/useOverlay'
import {useOverlayStack} from '@toolify/client/src/modules/modals/composables/useOverlayStack'
import {isElectron} from '@toolify/client/src/modules/electron/singletons/isElectron'
import {useNativeAppStore} from '@toolify/client/src/stores/AppTitlebarStore/useNativeAppStore'
import {
  provideStackableLayerPassive,
  provideStackableLayerPassThrough,
} from '@toolify/client/src/modules/routes/composables/useStackableLayerPassive'

const props = defineProps<{
  route: RouteLocationNormalizedLoadedGeneric
}>()

const stackableOutletName = computed(() => {
  const matched = props.route.matched
  for (let i = matched.length - 1; i >= 0; i--) {
    const record = matched[i]
    if (record.components && 'stackable' in record.components) {
      return 'stackable'
    }
  }
  return undefined
})

const resolvedOutletName = computed(() => stackableOutletName.value ?? 'default')

const routerViewDepth = computed(() => {
  const matched = props.route.matched
  const name = resolvedOutletName.value
  for (let i = matched.length - 1; i >= 0; i--) {
    if (matched[i].components?.[name]) {
      return i
    }
  }
  return 0
})

provide(viewDepthKey, routerViewDepth)

const nativeAppStore = useNativeAppStore()

const shouldOffsetForNativeHeader = computed(() => {
  return isElectron() && !nativeAppStore.isWindowInFullscreen
})

const rootRef = ref<HTMLElement | null>(null)
const isPassive = provideStackableLayerPassive()
const isPassThrough = provideStackableLayerPassThrough()
// Read when the layer's transition ends, not when it starts: the layer turns passive once its route is committed.
const getIsPassive = () => isPassive.value

const {zIndex} = useOverlay({
  element: rootRef,
  isOpen: () => !isPassive.value,
  onClose: () => {
    // TODO: Handle close
  },
})

const {hideAnchoredOverlaysBelow} = useOverlayStack()
let showAnchoredOverlaysBelow: (() => void) | null = null
function restoreAnchoredOverlaysBelow() {
  showAnchoredOverlaysBelow?.()
  showAnchoredOverlaysBelow = null
}
watch(isPassThrough, (value) => {
  restoreAnchoredOverlaysBelow()
  if (value && rootRef.value) {
    showAnchoredOverlaysBelow = hideAnchoredOverlaysBelow(rootRef.value)
  }
}, {flush: 'post'})
onBeforeUnmount(restoreAnchoredOverlaysBelow)
</script>

<template>
  <div
    ref="rootRef"
    class="stackable-router-view tw-w-full tw-fixed tw-inset-0"
    :class="{
      'is-offset-for-native-header': shouldOffsetForNativeHeader,
      'tw-pointer-events-auto': !isPassive && !isPassThrough,
      'tw-pointer-events-none': isPassive || isPassThrough,
    }"
    :style="{zIndex: zIndex ?? undefined}"
  >
    <CustomRouterView :route="props.route" :name="resolvedOutletName" v-slot="slotProps">
      <slot v-bind="slotProps" :getIsPassive="getIsPassive"/>
    </CustomRouterView>
  </div>
</template>

<style scoped lang="scss">
.stackable-router-view {
  height: 100%;

  &.is-offset-for-native-header {
    top: 2.25rem;
    height: calc(100% - 2.25rem);
  }
}
</style>
